import {
  AccountView,
  BeneficiaryView,
  CategoryView,
  CompanyView,
  DocumentView,
  EmployeeView,
  InvoiceEvidenceView,
  PayrollRecordView,
  PayrollView,
  TransactionView,
  VendorView,
} from '../../types/entity-types';
import { getRequest } from '../../utils/get-request';
import {
  ACCOUNT_API_PATH,
  BENEFICIARY_API_PATH,
  CATEGORY_API_PATH,
  COMPANY_API_PATH,
  DataContextValue,
  DataEntities,
  DOCUMENT_API_PATH,
  EMPLOYEE_API_PATH,
  EVIDENCE_API_PATH,
  PAYROLL_API_PATH,
  PAYROLL_RECORD_API_PATH,
  TRANSACTION_API_PATH,
  VENDOR_API_PATH,
} from './data-provider';

type SyncIncrementalUpdatesArgs = {
  mutateCache: DataContextValue['mutations']['mutateCache'];
  lastUpdated?: Record<keyof DataEntities, string>;
  entityData?: DataEntities;
};

export const syncIncrementalUpdates = async ({ mutateCache, lastUpdated, entityData }: SyncIncrementalUpdatesArgs) => {
  if (!lastUpdated || !mutateCache || !entityData) {
    return;
  }

  const {
    companies,
    accounts,
    transactions,
    vendors,
    categories,
    evidence,
    beneficiaries,
    documents,
    employees,
    payrolls,
    payrollRecords,
  } = entityData;

  const {
    companies: companyLastUpdated,
    accounts: accountLastUpdated,
    transactions: transactionLastUpdated,
    vendors: vendorLastUpdated,
    categories: categoryLastUpdated,
    evidence: evidenceLastUpdated,
    beneficiaries: beneficiaryLastUpdated,
    documents: documentLastUpdated,
    employees: employeeLastUpdated,
    payrolls: payrollLastUpdated,
    payrollRecords: payrollRecordLastUpdated,
  } = lastUpdated;

  const [
    incrementalCompanies,
    incrementalAccounts,
    incrementalTransactions,
    incrementalVendors,
    incrementalCategories,
    incrementalEvidence,
    incrementalBeneficiaries,
    incrementalDocuments,
    incrementalEmployees,
    incrementalPayrolls,
    incrementalPayrollRecords,
  ] = await Promise.all([
    getRequest<CompanyView[]>(`${COMPANY_API_PATH}?since=${companyLastUpdated}`),
    getRequest<AccountView[]>(`${ACCOUNT_API_PATH}?since=${accountLastUpdated}`),
    getRequest<TransactionView[]>(`${TRANSACTION_API_PATH}?since=${transactionLastUpdated}`),
    getRequest<VendorView[]>(`${VENDOR_API_PATH}?since=${vendorLastUpdated}`),
    getRequest<CategoryView[]>(`${CATEGORY_API_PATH}?since=${categoryLastUpdated}`),
    getRequest<InvoiceEvidenceView[]>(`${EVIDENCE_API_PATH}?since=${evidenceLastUpdated}`),
    getRequest<BeneficiaryView[]>(`${BENEFICIARY_API_PATH}?since=${beneficiaryLastUpdated}`),
    getRequest<DocumentView[]>(`${DOCUMENT_API_PATH}?since=${documentLastUpdated}`),
    getRequest<EmployeeView[]>(`${EMPLOYEE_API_PATH}?since=${employeeLastUpdated}`),
    getRequest<PayrollView[]>(`${PAYROLL_API_PATH}?since=${payrollLastUpdated}`),
    getRequest<PayrollRecordView[]>(`${PAYROLL_RECORD_API_PATH}?since=${payrollRecordLastUpdated}`),
  ]);

  const unchangedCompanies = getUnchangedEntities(companies, incrementalCompanies);
  const unchangedAccounts = getUnchangedEntities(accounts, incrementalAccounts);
  const unchangedTransactions = getUnchangedEntities(transactions, incrementalTransactions);
  const unchangedVendors = getUnchangedEntities(vendors, incrementalVendors);
  const unchangedCategories = getUnchangedEntities(categories, incrementalCategories);
  const unchangedEvidence = getUnchangedEntities(evidence, incrementalEvidence);
  const unchangedBeneficiaries = getUnchangedEntities(beneficiaries, incrementalBeneficiaries);
  const unchangedDocuments = getUnchangedEntities(documents, incrementalDocuments);
  const unchangedEmployees = getUnchangedEntities(employees, incrementalEmployees);
  const unchangedPayrolls = getUnchangedEntities(payrolls, incrementalPayrolls);
  const unchangedPayrollRecords = getUnchangedEntities(payrollRecords, incrementalPayrollRecords);

  const newCacheData = {
    companies: [...unchangedCompanies, ...incrementalCompanies],
    accounts: [...unchangedAccounts, ...incrementalAccounts],
    transactions: [...unchangedTransactions, ...incrementalTransactions],
    vendors: [...unchangedVendors, ...incrementalVendors],
    categories: [...unchangedCategories, ...incrementalCategories],
    evidence: [...unchangedEvidence, ...incrementalEvidence],
    beneficiaries: [...unchangedBeneficiaries, ...incrementalBeneficiaries],
    documents: [...unchangedDocuments, ...incrementalDocuments],
    employees: [...unchangedEmployees, ...incrementalEmployees],
    payrolls: [...unchangedPayrolls, ...incrementalPayrolls],
    payrollRecords: [...unchangedPayrollRecords, ...incrementalPayrollRecords],
  };

  await mutateCache(newCacheData);
};

const getUnchangedEntities = <T extends { id: string }>(entities: T[], incrementalEntities: T[]) => {
  const incrementalIds = incrementalEntities.map((e) => e.id);

  // TODO: https://linear.app/seapoint/issue/TENG-932/duplicate-transactions-with-different-ids and determine what is the best way to separate incremental vs existing transactions
  return entities.filter((entity) => !incrementalIds.includes(entity.id));
};
